import { FormApi } from "final-form";
import _ from "lodash";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { capitalizeFirstLetter } from "src/ui/utils/string";
import { makeStyles } from "tss-react/mui";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { AppPreference, Provisioning } from "@dashboard/provisioning/types";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { optionsType } from "../utils";

const initialValues: Partial<AppPreference> = {
    package: undefined,
    category: undefined,
    comment: null,
    type: "Bool",
    key: undefined,
    value: undefined,
};

const useStyles = makeStyles()((theme) => ({
    input: { flex: 1 },
    box: { display: "inline-flex", flex: 1, gap: theme.spacing(2) },
}));

type FormValues = AppPreference;
type ValidationErros = Omit<FormValues, "type"> & { type: string };

export const ApplicationPreferences = () => {
    const { value: initialValue, onChange } = useCollapsiblePersistence(
        "provisioningPage.collapsedSections",
        "ApplicationPreferences",
    );
    const { classes } = useStyles();
    const parentForm = useForm();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentValues } = useFormState<Partial<Provisioning>>();
    const preferences = parentValues?.settings?.appPreferences || [];

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const newValue = { ...values, comment: values.comment || null };
        if (preferences.length) {
            parentForm.change("settings.appPreferences", [...preferences, newValue]);
        }
        if (!preferences.length) {
            parentForm.change("settings.appPreferences", [newValue]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        return _.transform<FormValues, ValidationErros>(values, (result, value, key) => {
            if (!value && key !== "comment") {
                result[key] = t("pages:provisioningPage.input.preferenceComment.validation.empty", {
                    component: capitalizeFirstLetter(key),
                });
            }
        });
    };
    const handleDelete = (index: number) => {
        parentForm.change(
            "settings.appPreferences",
            preferences.filter((_item, i) => i !== index),
        );
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={initialValue} onChange={onChange}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>
                            {t("pages:provisioningPage.section.applicationPreferences.title")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <SectionDescription>
                            {t("pages:provisioningPage.section.applicationPreferences.description")}
                        </SectionDescription>
                        <MinimalTableContainer>
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <MinimalTableRow>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.package")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.category")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.type")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.key")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.value")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.comment")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableRow>
                                </MinimalTableHeader>
                                <MinimalTableBody>
                                    {preferences.length ? (
                                        preferences.map((item, index) => (
                                            <MinimalTableRow key={item.key + item.value}>
                                                <MinimalTableCell component="th" scope="row" align="left">
                                                    <Typography>{item.package}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.category}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.type}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.key}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.value}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.comment}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        onClick={() => handleDelete(index)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                        name="application-preference-delete"
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))
                                    ) : (
                                        <MinimalTableRow>
                                            <MinimalTableCell align="center" colSpan={7}>
                                                <Typography>
                                                    {t("pages:provisioningPage.table.noApplicationPreferences")}
                                                </Typography>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    )}
                                </MinimalTableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        <Row style={{ marginTop: theme.spacing(4), paddingBottom: theme.spacing(3) }}>
                            <TextField
                                name="package"
                                label={t("common:label.packageName")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <TextField
                                name="category"
                                label={t("pages:provisioningPage.input.categoryName.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                        </Row>
                        <Row style={{ paddingBottom: theme.spacing(3) }}>
                            <TextField name="key" label={t("common:label.key")} disabled={!provisionings.length} fullWidth />
                            <ControlledSelectField
                                name="type"
                                label={t("pages:provisioningPage.input.preferenceType.caption")}
                                options={optionsType.map((item) => ({
                                    label: item,
                                    value: item,
                                }))}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                        </Row>
                        <Row>
                            <TextField
                                name="value"
                                label={t("common:label.value")}
                                disabled={!provisionings.length}
                                className={classes.input}
                            />
                            <div className={classes.box}>
                                <TextField
                                    name="comment"
                                    label={t("common:label.comment")}
                                    disabled={!provisionings.length}
                                    className={classes.input}
                                />
                                <PrimaryOutlinedButton
                                    onClick={form.submit}
                                    disabled={!provisionings.length}
                                    name="application-preference-add"
                                >
                                    {t("common:button.add")}
                                </PrimaryOutlinedButton>
                            </div>
                        </Row>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
